import React, { useCallback } from "react";
import PropTypes from "prop-types";
import { Grid, makeStyles, Typography, Card, Button } from "@material-ui/core";
import productPageStyle from "features/products/styles";
import { useTranslation } from "react-i18next";
import { useMutationWithErrorHandling } from "hooks/errorHandling";
import { get } from "@totalsoft/change-tracking-react";
import { RECOVER_PRODUCT } from "../../mutations";
import { useApolloClient } from "@apollo/client";
import { invalidateApolloClientCache } from "utils/functions";
import { getTenant } from "providers/TenantProvider";
import { countryCodeConstants } from "features/common/constants";
import { addToast } from "components/notifications";

const useStyles = makeStyles(productPageStyle);

function ProductHistoryComponent(props) {
  const { quoteProductHistory, currencyCode, showAmountsWithVAT, onClose } = props;
  const { t } = useTranslation();
  const classes = useStyles();
  const client = useApolloClient();
  const { countryCode } = getTenant();

  const [cloneProduct] = useMutationWithErrorHandling(RECOVER_PRODUCT);

  const handleRecoverProduct = useCallback(async () => {
    const { data } = await cloneProduct({ variables: { quoteProductId: quoteProductHistory.quoteProductId |> get } });
    if (!data.recoverProduct) {
      addToast(t("ToastNotifications.ProductConfigurationNotValidAnymore"), "error");
      return;
    }

    invalidateApolloClientCache(
      client,
      { __typename: "Quote", quoteId: parseInt(quoteProductHistory.quoteId |> get) },
      ["quoteProducts", "quoteProductHistories"]
    );

    onClose();
  }, [cloneProduct, client, quoteProductHistory.quoteProductId, quoteProductHistory.quoteId, onClose, t]);

  return (
    <Card id={"productCard"} className={classes.card}>
      <Typography className={classes.modificationInfo}>
        {t("LONG_DATE", {
          date: quoteProductHistory.modifiedTimestamp |> get,
        })}{" "}
        {t("QuoteHistory.ModifiedBy")} {quoteProductHistory.firstName |> get} {quoteProductHistory.lastName |> get}
      </Typography>
      <Grid container>
        <Grid item xs={4} className={classes.productSection}>
          <div className={classes.arrowTipItem}>
            <Typography className={classes.productName}>{quoteProductHistory.productName |> get}</Typography>
            {quoteProductHistory.scheduleIsConfigurable &&
              countryCode !== countryCodeConstants.china &&
              countryCode !== countryCodeConstants.romania && (
                <Typography className={classes.scheduleType}>{t("Product.SeasonalSchedule")}</Typography>
              )}
            {!quoteProductHistory.scheduleIsConfigurable && countryCode === countryCodeConstants.brazil && (
              <Typography variant="body2" className={classes.scheduleType}>
                {t("Product.InstallmentAmount")}
              </Typography>
            )}
          </div>
        </Grid>
        <Grid
          item
          container
          xs={3}
          direction="column"
          className={
            quoteProductHistory.residualValueVisible |> get
              ? classes.paymentInfoWithResidual
              : classes.paymentInfoWithoutResidual
          }
        >
          <Typography className={classes.minFont}>
            {showAmountsWithVAT ? t("Product.DownPaymentWithVAT") : t("Product.DownPayment")}
          </Typography>
          {showAmountsWithVAT ? (
            <Typography className={classes.reducedFont}>
              {currencyCode} {quoteProductHistory.downPaymentAmountWithVAT |> get}
            </Typography>
          ) : (
            <Typography className={classes.reducedFont}>
              {currencyCode} {quoteProductHistory.downPaymentAmount |> get}
            </Typography>
          )}
          <Typography className={classes.minFont}>{t("Product.Term")}</Typography>
          <Typography className={classes.reducedFont}>{quoteProductHistory.duration |> get}</Typography>
          {(quoteProductHistory.residualValueVisible |> get) && (
            <>
              <Typography className={classes.minFont}>
                {showAmountsWithVAT ? t("Product.ResidualValueWithVAT") : t("Product.ResidualValue")}
              </Typography>
              {showAmountsWithVAT ? (
                <Typography className={classes.reducedFont}>
                  {currencyCode} {quoteProductHistory.residualValueAmountWithVAT |> get}
                </Typography>
              ) : (
                <Typography className={classes.reducedFont}>
                  {currencyCode} {quoteProductHistory.residualValueAmount |> get}
                </Typography>
              )}
            </>
          )}
        </Grid>
        <Grid item container xs={3} justifyContent="space-around" direction="column" className={classes.gradientLine}>
          <Grid item xs={6}>
            <Typography className={classes.minFont}>{t("QuoteHistory.Downloaded")}</Typography>
            <Typography className={classes.dateWidth}>
              {quoteProductHistory.downloadTimestamp |> get
                ? t("DATE_FORMAT", {
                    date: { value: quoteProductHistory.downloadTimestamp |> get, format: "DD.MM.YYYY" },
                  })
                : "-"}
            </Typography>
          </Grid>
          <Grid item xs={6}>
            <Typography className={classes.minFont}>{t("QuoteHistory.EmailedToClient")}</Typography>
            <Typography className={classes.dateWidth}>
              {quoteProductHistory.mailTimestamp |> get
                ? t("DATE_FORMAT", {
                    date: { value: quoteProductHistory.mailTimestamp |> get, format: "DD.MM.YYYY" },
                  })
                : "-"}
            </Typography>
          </Grid>
        </Grid>
        <Grid item container xs={2} justifyContent="space-around" alignItems="center">
          <Button variant="contained" color="primary" onClick={handleRecoverProduct}>
            {t("QuoteHistory.Recover")}
          </Button>
        </Grid>
      </Grid>
    </Card>
  );
}

ProductHistoryComponent.propTypes = {
  quoteProductHistory: PropTypes.object.isRequired,
  showAmountsWithVAT: PropTypes.bool.isRequired,
  currencyCode: PropTypes.string.isRequired,
  onClose: PropTypes.func.isRequired,
};

export default ProductHistoryComponent;
